import { useState } from "react";
import { Link } from "react-router-dom";
import { site } from "@/mocks/site";
import { categories } from "@/mocks/categories";

const SUBSCRIBE_URL = "https://readdy.ai/api/form/da20lk3k8r5te5mqsd3g";

export default function Footer() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [msg, setMsg] = useState("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = (fd.get("company_alt") as string)?.trim() ?? "";

    if (honeypot) {
      setStatus("success");
      setMsg("订阅成功，感谢关注！");
      form.reset();
      return;
    }
    fd.delete("company_alt");
    if (!fd.get("email")) return;

    setStatus("idle");
    try {
      const res = await fetch(SUBSCRIBE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const bodyMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const ok = res.ok && parsed?.code === "OK";
      if (ok) {
        setStatus("success");
        setMsg("订阅成功，娱乐圈新鲜事将第一时间送达！");
        form.reset();
      } else {
        setStatus("error");
        setMsg(bodyMsg || "订阅失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMsg("网络异常，请稍后重试。");
    }
  };

  return (
    <footer className="border-t border-background-200 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* Brand + newsletter */}
          <div className="lg:col-span-5">
            <Link to="/" className="flex items-center gap-2.5" aria-label="吃瓜视频网 首页">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
                <i className="ri-fire-line text-lg" />
              </span>
              <span className="font-heading text-lg font-bold text-foreground-950">
                {site.name}
              </span>
            </Link>
            <p className="mt-4 max-w-md text-sm leading-6 text-foreground-600">
              {site.description}
            </p>
            <form onSubmit={onSubmit} data-readdy-form="chigua-subscribe" className="mt-6 max-w-md">
              <label htmlFor="footer-email" className="text-sm font-medium text-foreground-800">
                订阅圈内速递，不错过任何新鲜事
              </label>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <input
                  id="footer-email"
                  type="email"
                  name="email"
                  required
                  placeholder="输入你的邮箱"
                  className="w-full flex-1 rounded-full bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none ring-1 ring-inset ring-background-300 focus:ring-2 focus:ring-primary-300"
                />
                <button
                  type="submit"
                  className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
                >
                  立即订阅
                </button>
              </div>
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />
              {status === "success" && (
                <p className="mt-2 text-sm text-secondary-600">{msg}</p>
              )}
              {status === "error" && (
                <p className="mt-2 text-sm text-primary-600">{msg}</p>
              )}
            </form>
          </div>

          {/* Columns */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold text-foreground-900">栏目导航</h4>
            <ul className="mt-4 space-y-2.5">
              {categories.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/columns/${c.slug}`}
                    className="text-sm text-foreground-600 transition hover:text-primary-600"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick links */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold text-foreground-900">关于我们</h4>
            <ul className="mt-4 space-y-2.5">
              <li><Link to="/about" className="text-sm text-foreground-600 transition hover:text-primary-600">品牌介绍</Link></li>
              <li><Link to="/about#principles" className="text-sm text-foreground-600 transition hover:text-primary-600">内容原则</Link></li>
              <li><Link to="/about#feedback" className="text-sm text-foreground-600 transition hover:text-primary-600">用户口碑</Link></li>
              <li><Link to="/contact" className="text-sm text-foreground-600 transition hover:text-primary-600">联系我们</Link></li>
              <li><Link to="/contact" className="text-sm text-foreground-600 transition hover:text-primary-600">我要投稿</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h4 className="text-sm font-semibold text-foreground-900">联系方式</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-foreground-600">
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-primary-500" /> {site.contact.email}
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-phone-line text-primary-500" /> {site.contact.phone}
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-wechat-line text-secondary-500" /> {site.contact.wechat}
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-weibo-line text-accent-500" /> {site.contact.weibo}
              </li>
            </ul>
            <div className="mt-5 flex gap-3">
              <a href="#" aria-label="微信" className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50 ring-1 ring-inset ring-background-300 text-foreground-600 hover:text-primary-600">
                <i className="ri-wechat-line" />
              </a>
              <a href="#" aria-label="微博" className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50 ring-1 ring-inset ring-background-300 text-foreground-600 hover:text-primary-600">
                <i className="ri-weibo-line" />
              </a>
              <a href="#" aria-label="小红书" className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50 ring-1 ring-inset ring-background-300 text-foreground-600 hover:text-primary-600">
                <i className="ri-community-line" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-background-200">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-5 text-xs text-foreground-500 sm:flex-row md:px-6">
          <p>© {new Date().getFullYear()} {site.name} · 娱乐八卦与圈内速递平台</p>
          <p className="flex items-center gap-2">
            <i className="ri-video-line" /> 追星吃瓜，一站搞定
          </p>
        </div>
      </div>
    </footer>
  );
}